import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  Paper,
  alpha,
  IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Plus, Bot, Zap, X, UploadCloud, Cloud, Calendar } from 'lucide-react';
import BotList from '../components/BotList';
import { botsApi } from '../../../api/bots.api';
import useConfirm from '../../../hooks/useConfirm';
import { useUIStore } from '../../../store';

const parseSettings = (value) => {
  if (!value || typeof value !== 'string') return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
};

const mapBot = (bot) => {
  const settings = parseSettings(bot?.b_setting);
  const isActive = Number(bot?.b_active ?? bot?.b_status ?? 0) === 1;
  return {
    id: bot?.bid,
    name: bot?.b_name || '',
    description: settings.description || '',
    isActive,
    status: isActive ? 'active' : 'inactive',
    createdAt: bot?.b_created || bot?.created_at,
    updatedAt: bot?.b_updated || bot?.updated_at,
    raw: bot,
  };
};

const nameRegex = /^[A-Za-z0-9_]+$/;

const BotsPage = () => {
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  const showSnackbar = useUIStore((state) => state.showSnackbar);
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [saving, setSaving] = useState(false);
  const [formValues, setFormValues] = useState({
    id: '',
    name: '',
    description: '',
  });

  const loadBots = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await botsApi.getAll();
      const rows = response?.data?.data || response?.data || [];
      const normalized = Array.isArray(rows) ? rows.map(mapBot) : [];
      setBots(normalized);
    } catch (err) {
      setError('Failed to load bots. Please try again.');
      setBots([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBots();
  }, [loadBots]);

  const handleOpenCreate = () => {
    setModalMode('create');
    setFormValues({ id: '', name: '', description: '' });
    setModalOpen(true);
  };

  const handleOpenEdit = (bot) => {
    setModalMode('edit');
    setFormValues({
      id: bot?.id || '',
      name: bot?.name || '',
      description: bot?.description || '',
    });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    if (saving) return;
    setModalOpen(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;

    const name = formValues.name.trim();
    if (name.length < 2 || name.length > 100 || !nameRegex.test(name)) {
      setError('Bot name must be 2-100 characters and only contain letters, numbers, or _.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      if (modalMode === 'create') {
        const createResponse = await botsApi.create({ name });
        const created = createResponse?.data?.data || createResponse?.data;
        const botId = created?.bid || created?.id;
        if (botId && formValues.description.trim()) {
          await botsApi.update(botId, { description: formValues.description.trim() });
        }
      } else {
        await botsApi.update(formValues.id, {
          name,
          description: formValues.description.trim(),
        });
      }
      setModalOpen(false);
      await loadBots();
    } catch (err) {
      setError('Failed to save bot. Please check the name and try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (bot) => {
    if (!bot?.id) return;
    const confirmText = `Delete bot "${bot.name}"? This cannot be undone.`;
    const isConfirmed = await confirm({
      title: 'Delete Bot',
      message: confirmText,
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'error',
    });
    if (!isConfirmed) return;

    setError('');
    try {
      await botsApi.delete(bot.id);
      showSnackbar({ message: 'Bot deleted successfully', severity: 'success' });
      await loadBots();
    } catch (err) {
      setError('Failed to delete bot. Please try again.');
    }
  };

  const handleOpenBuilder = (bot) => {
    if (!bot?.id) return;
    navigate(`/botbuilder?botId=${bot.id}`);
  };

  const handleOpenSettings = (bot) => {
    if (!bot?.id) return;
    navigate(`/botsettings?botId=${bot.id}`);
  };

  const handleOpenMedia = () => {
    navigate('/botmedia');
  };

  const handleOpenMetaMedia = () => {
    navigate('/botmedia-meta');
  };

  const handleOpenAppointmentFlow = () => {
    navigate('/appointments');
  };

  const headerStats = useMemo(() => {
    const total = bots.length;
    const active = bots.filter((bot) => bot.isActive).length;
    return { total, active };
  }, [bots]);

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        minHeight: '100vh',
        bgcolor: (theme) => alpha(theme.palette.grey[100], 0.5),
      }}
    >
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: { xs: 2.5, sm: 3 },
          borderRadius: 4,
          background: (theme) => `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          spacing={3}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: (theme) => `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            >
              <Bot size={28} color="#fff" />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.5rem', sm: '1.75rem' },
                  color: 'text.primary',
                  letterSpacing: '-0.02em',
                }}
              >
                Bots
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mt: 0.25,
                }}
              >
                Build and manage automated WhatsApp flows in one place.
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center">
            {/* Stats */}
            <Stack direction="row" spacing={1}>
              <Paper
                elevation={0}
                sx={{
                  px: 2.5,
                  py: 1.5,
                  borderRadius: 2.5,
                  bgcolor: (theme) => alpha(theme.palette.grey[100], 0.8),
                  border: '1px solid',
                  borderColor: 'divider',
                  minWidth: 80,
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.65rem',
                  }}
                >
                  Total
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    lineHeight: 1.2,
                  }}
                >
                  {headerStats.total}
                </Typography>
              </Paper>
              <Paper
                elevation={0}
                sx={{
                  px: 2.5,
                  py: 1.5,
                  borderRadius: 2.5,
                  bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
                  border: '1px solid',
                  borderColor: (theme) => alpha(theme.palette.success.main, 0.2),
                  minWidth: 80,
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'success.dark',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.65rem',
                  }}
                >
                  Active
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: 'success.dark',
                    lineHeight: 1.2,
                  }}
                >
                  {headerStats.active}
                </Typography>
              </Paper>
            </Stack>

            {/* Action Buttons */}
            <Button
              variant="outlined"
              startIcon={<Cloud size={18} />}
              onClick={handleOpenMetaMedia}
              sx={{
                borderRadius: 2.5,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
              }}
            >
              Meta Media
            </Button>
            <Button
              variant="contained"
              startIcon={<Calendar size={18} />}
              onClick={handleOpenAppointmentFlow}
              sx={{
                borderRadius: 2.5,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
              }}
            >
              Appointment
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-icon': {
              alignItems: 'center',
            },
          }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {/* Bot List */}
      {loading ? (
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 10,
            borderRadius: 4,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress size={40} thickness={4} />
            <Typography variant="body2" color="text.secondary">
              Loading your bots...
            </Typography>
          </Stack>
        </Paper>
      ) : (
        <BotList
          bots={bots}
          loading={loading}
          onCreateBot={handleOpenCreate}
          onEditBot={handleOpenEdit}
          onDeleteBot={handleDelete}
          onSettingsBot={handleOpenSettings}
          onViewBot={handleOpenBuilder}
        />
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 24px 48px rgba(0,0,0,0.16)',
          },
        }}
      >
        <Box component="form" onSubmit={handleSubmit}>
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pb: 1,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                }}
              >
                {modalMode === 'create' ? (
                  <Zap size={20} color="#1976d2" />
                ) : (
                  <Bot size={20} color="#1976d2" />
                )}
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {modalMode === 'create' ? 'Create New Bot' : 'Edit Bot'}
              </Typography>
            </Stack>
            <IconButton
              onClick={handleCloseModal}
              disabled={saving}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: (theme) => alpha(theme.palette.grey[500], 0.1),
                },
              }}
            >
              <X size={20} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={3} mt={1}>
              <TextField
                label="Bot Name"
                placeholder="My_WhatsApp_Bot"
                value={formValues.name}
                onChange={(e) => setFormValues((prev) => ({ ...prev, name: e.target.value }))}
                inputProps={{ minLength: 2, maxLength: 100 }}
                helperText="Use letters, numbers, and underscore only."
                required
                autoFocus
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
              <TextField
                label="Short Description"
                placeholder="Describe what this bot does..."
                value={formValues.description}
                onChange={(e) => setFormValues((prev) => ({ ...prev, description: e.target.value }))}
                multiline
                minRows={3}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
            <Button
              onClick={handleCloseModal}
              disabled={saving}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 2.5,
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                },
              }}
            >
              {saving ? 'Saving...' : modalMode === 'create' ? 'Create Bot' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default BotsPage;
